/**
 * Default QueryClient factory with a global `meta.invalidates` handler.
 *
 * The codebase convention: mutation hooks declare which query keys should
 * refetch on success via the standard TanStack `meta` field:
 *
 *     meta: { invalidates: [qk.payroll.runs.all(slug)] }
 *
 * TanStack Query passes `meta` through unchanged — it has no built-in
 * handling. Before this helper, every `meta.invalidates: [...]` declaration
 * across the 14 mutation sites in @linkhr/api-client/hooks was dead code:
 * the mutation succeeded, the cached query stayed stale, and the UI didn't
 * refresh until a full page reload (verified in the wild on Approve & Lock /
 * Recall against payroll runs).
 *
 * `createDefaultQueryClient` wires a `MutationCache` whose global
 * `onSuccess` reads `meta.invalidates` and calls `invalidateQueries`
 * once per declared key prefix. Apps that pass their own QueryClient
 * still get this behavior by calling this factory.
 *
 * Optional `meta.invalidationType: "active" | "all" | "none"` — passed
 * through to `invalidateQueries({ refetchType })`. Default `"active"`
 * (TanStack default).
 *
 * Optional `meta.awaitInvalidation: true` — block the mutation's promise
 * resolution until all invalidations settle. Default false (fire and forget).
 *
 * @example
 *   // App-side:
 *   const queryClient = createDefaultQueryClient({
 *     queries: { staleTime: 30_000 },
 *   });
 *
 *   // Hook-side (already done across the codebase — no change needed):
 *   return useTenantMutation(client, {
 *     mutationFn: ...,
 *     meta: { invalidates: [qk.payroll.runs.all(slug)] },
 *   });
 */
import {
  MutationCache,
  QueryClient,
  type DefaultOptions,
  type QueryKey,
} from "@tanstack/react-query";

/**
 * Strongly-typed shape of the mutation `meta` field this codebase relies on.
 * Hook authors should annotate their `useTenantMutation` `meta` block against
 * this type to get autocomplete + a compile-time guard against typos.
 */
export interface InvalidatesMeta {
  /**
   * Query key prefixes to invalidate after a successful mutation.
   * Each entry is passed to `qc.invalidateQueries({ queryKey })`.
   * TanStack matches by prefix, so a key like `[slug, "payroll", "runs"]`
   * invalidates every query starting with that array (list, detail,
   * payslips, etc.).
   */
  invalidates?: ReadonlyArray<QueryKey>;
  /**
   * `"active"` (default) — only refetches mounted queries; inactive cache
   * entries are merely marked stale. `"all"` — refetches everything,
   * including unmounted queries that may remount soon.
   */
  invalidationType?: "active" | "all" | "none";
  /**
   * `true` (default `false`) — block the mutation's promise resolution until
   * all invalidations settle. Use when downstream code (e.g. a navigation
   * after success) needs the latest data already in cache.
   */
  awaitInvalidation?: boolean;
}

type RawMeta = Record<string, unknown> | undefined;

function readInvalidatesMeta(meta: RawMeta): InvalidatesMeta | null {
  if (!meta) return null;
  const keys = meta.invalidates;
  if (!Array.isArray(keys) || keys.length === 0) return null;
  const refetchType = meta.invalidationType;
  const awaitFlag = meta.awaitInvalidation;
  return {
    invalidates: keys as ReadonlyArray<QueryKey>,
    ...(refetchType === "active" || refetchType === "all" || refetchType === "none"
      ? { invalidationType: refetchType }
      : {}),
    ...(typeof awaitFlag === "boolean" ? { awaitInvalidation: awaitFlag } : {}),
  };
}

/**
 * Build a QueryClient with HR-friendly defaults + the meta-invalidates
 * mutation cache wired in.
 *
 * The MutationCache and QueryClient hold mutual references — the cache's
 * `onSuccess` needs the client to call `invalidateQueries`. We construct
 * the client first with a stub cache, then build the real cache against
 * the client closure, then swap. Standard TanStack v5 pattern.
 */
export function createDefaultQueryClient(overrides?: DefaultOptions): QueryClient {
  // Late-bound closure — assigned after QueryClient construction.
  // `let qc!` tells TS we promise to assign before the cache callback fires.
  // ESLint flags this as `prefer-const` (one assignment, technically), but the
  // forward-declaration pattern is required so the cache callback can close
  // over the client BEFORE the client is constructed — chicken-and-egg.
  // eslint-disable-next-line prefer-const
  let qc!: QueryClient;

  const mutationCache = new MutationCache({
    onSuccess: (_data, _vars, _ctx, mutation) => {
      const meta = readInvalidatesMeta(mutation.options.meta);
      if (meta === null || meta.invalidates === undefined) return undefined;
      const refetchType = meta.invalidationType ?? "active";

      // Skip invalidation entirely when explicitly opted out — useful for
      // mutations whose result already replaces the cached value via
      // `setQueryData` (no refetch needed) but still want to document the
      // key relationship for grep-ability.
      if (refetchType === "none") return undefined;

      const invalidations = meta.invalidates.map((queryKey) =>
        qc.invalidateQueries({ queryKey, refetchType }),
      );

      if (meta.awaitInvalidation === true) {
        // Returning a promise from onSuccess makes TanStack await it before
        // resolving the mutation's `mutateAsync` — callers that need fresh
        // data already cached after `await mutation.mutateAsync(...)` should
        // opt in via `meta.awaitInvalidation = true`.
        return Promise.all(invalidations).then(() => undefined);
      }
      // Fire and forget — invalidation runs in the background; the mutation
      // promise resolves immediately. UI re-renders via TanStack's normal
      // subscription model once queries refetch.
      void Promise.all(invalidations);
      return undefined;
    },
  });

  qc = new QueryClient({
    mutationCache,
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, err) => {
          if (
            err &&
            typeof err === "object" &&
            "status" in err &&
            typeof (err as { status: unknown }).status === "number"
          ) {
            const status = (err as { status: number }).status;
            return status >= 500 && failureCount < 2;
          }
          return failureCount < 1;
        },
        ...overrides?.queries,
      },
      mutations: { retry: false, ...overrides?.mutations },
    },
  });

  return qc;
}
